import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import EditIcon from '@atlaskit/icon/core/edit';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import AiGenerativeChaptersIcon from '@atlaskit/icon-lab/core/ai-generative-chapters';
import {
	type BaseConversationAction,
	type ChatAction,
	type ConversationAction,
	type SkillAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { useAudioPlayerStore } from '@atlassian/ai-audio/hooks/use-audio-player-store';

import { FooterCommentsSummaryCombined } from '@confluence/ai-comments-summary/entry-points/FooterCommentsSummaryCombined.ts';
import { CommentsSummarySource } from '@confluence/ai-comments-summary/entry-points/CommentsSummaryContent.ts';
import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';
import { useConfluenceAutomationNudgeActions } from '@confluence/automation-discovery/entry-points/useConfluenceAutomationNudgeActions.ts';
import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import {
	useAudioFeatureFlags,
	BriefingNudgeExperimentState,
} from '@confluence/audio/entry-points/useAudioFeatureFlags.ts';
import { useBulkIssueCreate } from '@confluence/bulk-issue-create/entry-points/useBulkIssueCreate.ts';
import {
	CommentCountsContext,
	CommentCountsProvider,
} from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialogContextProvider.ts';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import { PageCatchupComponent } from '@confluence/page-catchup/entry-points/PageCatchupStreamingEntryPoint.tsx';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary/entry-points/QuickSummaryStreamingEntryPoint.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import type { PageRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.tsx';
import { shouldShowGetProjectOverviewAction } from '../utils/shouldShowGetProjectOverviewAction.ts';
import { useAutomationNudgeActions } from '@confluence/automation-discovery/entry-points/useAutomationNudgeActions.ts';

export const messages: {
	inlineCommentsMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageCatchupMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageCatchupMenuItemSubheaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	summarizeSectionLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkIssueCreateMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkWorkItemsCreateMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingMenuItemLabelWithContentType: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingContentTypePage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingContentTypeBlogPost: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingContentTypeLiveDoc: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	audioBriefingMenuItemSubheaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	getProjectOverviewMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftCommentsMenuItemLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftCommentsPrompt: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	pageCatchupMenuItemSubheaderLabel: {
		id: 'page-rovo-config-common.menu.item.label.page-catchup-subheader',
		defaultMessage: "See what's changed since your last visit",
		description: 'Label for the AI Content Actions Page Catchup Menu Item Subheader',
	},
	summarizeSectionLabel: {
		id: 'page-rovo-config-common.summarize.section.label',
		defaultMessage: 'Summarize page',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	audioBriefingMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing',
		defaultMessage: 'Listen to page',
		description:
			'The text is shown as the label for a drop-down menu item in the Rovo actions menu on the page view. When selected, it starts an audio briefing that reads the page content aloud to the user.',
	},
	audioBriefingMenuItemLabelWithContentType: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-with-content-type',
		defaultMessage: 'Listen to {contentType}',
		description:
			'The text is shown as the label for a drop-down menu item in the Rovo actions menu on the page view. The contentType is dynamically set to page, live doc, or blogpost based on the content being viewed.',
	},
	audioBriefingContentTypePage: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-content-type-page',
		defaultMessage: 'page',
		description:
			'Content type label for audio briefing when the content is a page. Used in combination with audioBriefingMenuItemLabelWithContentType to produce "Listen to page".',
	},
	audioBriefingContentTypeBlogPost: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-content-type-blogpost',
		defaultMessage: 'blogpost',
		description:
			'Content type label for audio briefing when the content is a blog post. Used in combination with audioBriefingMenuItemLabelWithContentType to produce "Listen to blogpost".',
	},
	audioBriefingContentTypeLiveDoc: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-content-type-live-doc',
		defaultMessage: 'live doc',
		description:
			'Content type label for audio briefing when the content subtype is live. Used in combination with audioBriefingMenuItemLabelWithContentType to produce "Listen to live doc".',
	},
	audioBriefingMenuItemSubheaderLabel: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-subheader',
		defaultMessage: 'Get a brief audio summary',
		description: 'Label for the Audio Briefing Menu Item Subheader',
	},
	getProjectOverviewMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.get-project-overview',
		defaultMessage: 'Get Project Overview',
		description: 'Label for the Get Project Overview skill action Menu Item',
	},
	draftCommentsMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.draft-comments',
		defaultMessage: 'Draft a comment',
		description:
			'Label for the Draft Comments Menu Item in the Rovo Chat actions list. Clicking this starts a conversation to draft a comment on the page.',
	},
	draftCommentsPrompt: {
		id: 'page-rovo-config-common.menu.item.prompt.draft-comments',
		defaultMessage:
			'Suggest a few comments I could leave on this page, then offer to post my chosen one.',
		description:
			"Prompt sent to Rovo Chat when a user clicks the Draft a comment action. Rovo will suggest several comment options and offer to post the chosen one on the user's behalf.",
	},
});

const AUDIO_BRIEFING_CONTENT_TYPE_MESSAGES = {
	live: messages.audioBriefingContentTypeLiveDoc,
	page: messages.audioBriefingContentTypePage,
	blogpost: messages.audioBriefingContentTypeBlogPost,
} as const;

type AudioBriefingContentType = keyof typeof AUDIO_BRIEFING_CONTENT_TYPE_MESSAGES;

const isValidAudioContentTye = (key: string): key is AudioBriefingContentType =>
	key in AUDIO_BRIEFING_CONTENT_TYPE_MESSAGES;

type ViewPageRovoActionsReturnType = {
	actionId: string;
	action: ConversationAction;
}[];

export const useViewPageRovoActions = (): ViewPageRovoActionsReturnType => {
	const {
		contentId,
		contentType,
		contentTitle,
		spaceKey,
		lastModifiedDate,
		isUnpublishedDraft,
		isEditorEmpty,
	} = useRovoConfigProviderProps() as PageRovoConfigProviderProps;

	const { mode: rovoUiMode, toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();

	const intl = useIntl();
	const { formatMessage } = intl;
	const automationNudgeActionsDEPRECATED = useConfluenceAutomationNudgeActions(contentId);
	const automationNudgeActions = useAutomationNudgeActions();

	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const pageMode = useGetPageMode();

	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	// Page Catchup action

	const pageCatchupAction = useCallback((): BaseConversationAction | SkillAction => {
		if (expValEquals('confluence_content_catchup_as_a_rovo_skill', 'isEnabled', true)) {
			return {
				id: 'summarize-changes-ai-action',
				title: formatMessage(messages.pageCatchupMenuItemLabel),
				secondaryTitle: formatMessage(messages.pageCatchupMenuItemSubheaderLabel),
				icon: () => <ClockIcon label="" testId="summarize-changes-ai-action.icon" />,
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: '/content-catchup',
				nudgeContext: {
					id: 'content-catchup',
				},
			};
		}

		return {
			id: 'summarize-changes-ai-action',
			title: formatMessage(messages.pageCatchupMenuItemLabel),
			secondaryTitle: formatMessage(messages.pageCatchupMenuItemSubheaderLabel),
			icon: () => <ClockIcon label="" testId="summarize-changes-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<PageCatchupComponent
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'content-catchup',
			},
		};
	}, [formatMessage, contentId, contentType, spaceKey]);

	// Summarize page action

	const quickSummaryAction = useCallback((): BaseConversationAction | SkillAction => {
		if (expValEquals('confluence_quick_summary_as_a_skill', 'isEnabled', true)) {
			return {
				id: 'summarize-page-ai-action',
				title: formatMessage(messages.summarizeSectionLabel),
				icon: () => <ListBulletedIcon label="" testId="summarize-page-ai-action.icon" />,
				type: 'skill-action',
				handleType: HandleActionType.Skill,
				staticPrompt: '/quick-summary',
				nudgeContext: {
					id: 'page-summaries',
				},
			};
		}

		return {
			id: 'summarize-page-ai-action',
			title: formatMessage(messages.summarizeSectionLabel),
			icon: () => <ListBulletedIcon label="" testId="summarize-page-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<QuickSummaryStreamingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'page-summaries',
			},
		};
	}, [formatMessage, contentId, contentType, spaceKey, lastModifiedDate]);

	// Summarize comments action

	const commentSummaryAction = useCallback(
		(): BaseConversationAction => ({
			id: 'summarize-comments-ai-action',
			title: formatMessage(messages.inlineCommentsMenuItemLabel),
			icon: () => <CommentIcon label="" />,
			viewConfig: {
				render: () => (
					<CommentCountsProvider contentId={contentId}>
						<FooterCommentsSummaryCombined
							contentId={contentId}
							contentType={contentType}
							source={CommentsSummarySource.Footer}
							spaceKey={spaceKey}
							isInObjectSidebar
							showAIDisclaimer={false}
						/>
					</CommentCountsProvider>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
		}),
		[formatMessage, contentId, contentType, spaceKey],
	);

	// Create Jira bulk issues action

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
		useRovoWidth: true,
	});

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else {
			void handleBulkIssueCreate();
		}
	}, [isSidePanelOpen, showWarningDialog, handleBulkIssueCreate]);

	const bulkIssueCreateAction = useCallback(
		(): BaseConversationAction => ({
			id: 'bulk-issue-create-ai-action',
			title: formatMessage(
				fg('confluence-issue-terminology-refresh')
					? messages.bulkWorkItemsCreateMenuItemLabel
					: messages.bulkIssueCreateMenuItemLabel,
			),
			icon: () => <WorkItemsIcon label="" />,
			onClick: onBulkIssueCreate,
			type: 'ai-action',
			handleType: HandleActionType.Base,
		}),
		[formatMessage, onBulkIssueCreate],
	);

	// Audio briefing action

	const { openAudioPlayer } = useAudioPlayerStore();
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const { activeContent } = useAudioButtonWrapperStore();
	const { audioBriefingNudgeExperimentState } = useAudioFeatureFlags();
	const playBriefingImmediately =
		audioBriefingNudgeExperimentState === BriefingNudgeExperimentState.NUDGE_AUTOPLAY;

	const onAudioBriefingAction = useCallback(() => {
		if (!!activeContent) {
			openAudioPlayer(activeContent, { playImmediately: playBriefingImmediately });

			// Close the Rovo popup or sidebar after clicking
			const isSidebarOrMiniModalOpen =
				rovoUiMode === ConversationAssistantUIModes.SIDEBAR ||
				rovoUiMode === ConversationAssistantUIModes.MINI_MODAL;

			if (isSidebarOrMiniModalOpen) {
				// Close sidebar
				toggleConversationAssistantUIMode({
					targetMode: undefined,
					sidebarApi,
				});
			}
		}
	}, [
		activeContent,
		openAudioPlayer,
		playBriefingImmediately,
		rovoUiMode,
		toggleConversationAssistantUIMode,
		sidebarApi,
	]);

	const audioBriefingAction = useCallback((): BaseConversationAction => {
		const key = activeContent?.subType || activeContent?.contentType;
		const contentTypeMessage =
			key && isValidAudioContentTye(key) ? AUDIO_BRIEFING_CONTENT_TYPE_MESSAGES[key] : undefined;
		const contentTypeLabel = contentTypeMessage ? formatMessage(contentTypeMessage) : '';

		const title = contentTypeLabel
			? formatMessage(messages.audioBriefingMenuItemLabelWithContentType, {
					contentType: contentTypeLabel,
				})
			: formatMessage(messages.audioBriefingMenuItemLabel);

		return {
			id: 'audio-briefing-ai-action',
			title,
			secondaryTitle: formatMessage(messages.audioBriefingMenuItemSubheaderLabel),
			icon: () => <AiGenerativeAudioBriefingIcon label="" />,
			onClick: onAudioBriefingAction,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: 'proactive-audio-briefing', // PROACTIVE_AI_AUDIO_BRIEFING in convo-ai
				suppressionKey: activeContent?.contentId,
			},
		};
	}, [formatMessage, onAudioBriefingAction, activeContent]);

	const shouldShowAudioBriefingAction = useMemo(
		() =>
			!!activeContent && audioBriefingNudgeExperimentState !== BriefingNudgeExperimentState.CONTROL,
		[activeContent, audioBriefingNudgeExperimentState],
	);

	// Get Project Overview skill action

	const getProjectOverviewAction = useCallback(
		(): SkillAction => ({
			id: 'get-project-overview-ai-action',
			title: formatMessage(messages.getProjectOverviewMenuItemLabel),
			icon: () => <AiGenerativeChaptersIcon label="" />,
			handleType: HandleActionType.Skill,
			type: 'skill-action',
			staticPrompt: '/get-project-details',
		}),
		[formatMessage],
	);

	// Draft comment action

	const draftCommentAction = useCallback(
		(): ChatAction => ({
			id: 'draft-comment-ai-action',
			title: formatMessage(messages.draftCommentsMenuItemLabel),
			icon: () => <EditIcon label="" />,
			handleType: HandleActionType.Chat,
			staticPrompt: formatMessage(messages.draftCommentsPrompt),
		}),
		[formatMessage],
	);

	// Final actions list

	const getViewPageRovoActions = useCallback(() => {
		const actions: { actionId: string; action: ConversationAction }[] = [];

		const isPageOrBlogpost = contentType === 'page' || contentType === 'blogpost';

		// needed for space overview since it doesn't have content type
		// also skip for unpublished drafts and empty live docs
		if (!isPageOrBlogpost || isUnpublishedDraft || isEditorEmpty) {
			return actions;
		}

		// Get Project Overview action (top of the list for published pages with project-related title)
		if (
			shouldShowGetProjectOverviewAction({
				contentType,
				contentTitle,
			}) &&
			expVal('rovo_connie_empty_state_get_project_details_skill', 'isEnabled', false)
		) {
			actions.push({ actionId: 'get-project-overview', action: getProjectOverviewAction() });
		}

		actions.push(
			{ actionId: 'page-catchup', action: pageCatchupAction() },
			{ actionId: 'quick-summary', action: quickSummaryAction() },
		);

		actions.push(
			...(fg('cc_automations_nudge_actions_error_boundary')
				? automationNudgeActions
				: automationNudgeActionsDEPRECATED),
		);

		if (expValEquals('confluence_rovo_draft_comments', 'isEnabled', true)) {
			actions.push({ actionId: 'draft-comment', action: draftCommentAction() });
		}

		if (shouldShowAudioBriefingAction) {
			actions.push({ actionId: 'audio-briefing', action: audioBriefingAction() });
		}

		// Only show comment summary in Rovo button when proactive_comment_summary experiment is disabled
		if (
			(unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) &&
			!expValEquals('proactive_comment_summary', 'isEnabled', true)
		) {
			actions.push({ actionId: 'comment-summary', action: commentSummaryAction() });
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			actions.push({ actionId: 'bulk-issue-create', action: bulkIssueCreateAction() });
		}

		return actions;
	}, [
		contentType,
		contentTitle,
		bulkIssueCreateAction,
		commentSummaryAction,
		draftCommentAction,
		footerCommentsCount,
		getProjectOverviewAction,
		isUnpublishedDraft,
		isEditorEmpty,
		pageCatchupAction,
		pageMode,
		quickSummaryAction,
		automationNudgeActions,
		automationNudgeActionsDEPRECATED,
		unresolvedInlineCommentsCount,
		audioBriefingAction,
		shouldShowAudioBriefingAction,
	]);

	const actions = useMemo(() => getViewPageRovoActions(), [getViewPageRovoActions]);

	return actions;
};
